import React, { useState, useMemo, useEffect } from 'react';
import { 
  ArrowLeft, Send, Mail, MessageSquare, Smartphone, Users,
  Check, ChevronDown, Bold, Italic, Link, Image, List, 
  AlignLeft, AlignCenter, AlignRight, Undo, Redo
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { csvCustomers, zoneColors } from '../data/csvData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SendPage = ({ onBack, selectedCustomers = [] }) => {
  const [notifyUsing, setNotifyUsing] = useState('email');
  const [showDropdown, setShowDropdown] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('StreakFree');
  const [messageContent, setMessageContent] = useState(`Hi [firstname]

${companyName}`);
  const [subject, setSubject] = useState(`Message from ${companyName}`);

  // Load company settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        if (!token) return;
        
        const res = await fetch(`${API_URL}/api/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.company_logo) setCompanyLogo(data.company_logo);
          if (data.company_name) {
            setCompanyName(data.company_name);
            setSubject(`Message from ${data.company_name}`);
            setMessageContent(`Hi [firstname]

${data.company_name}`);
          }
        }
      } catch (err) {
        console.error('Error loading settings:', err);
      }
    };
    loadSettings();
  }, []);

  // Get recipients - either passed in or default to customers with email
  const recipients = useMemo(() => {
    if (selectedCustomers.length > 0) {
      return selectedCustomers;
    }
    // Default to first 5 customers with email
    return csvCustomers.filter(c => c.email).slice(0, 5);
  }, [selectedCustomers]);

  const notifyOptions = [
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'sms', label: 'SMS', icon: Smartphone },
    { id: 'both', label: 'Email & Sms', icon: Send },
  ];

  // Calculate character count and SMS credits
  const charCount = messageContent.length;
  const wordCount = messageContent.trim().split(/\s+/).filter(w => w).length;
  const smsCredits = Math.ceil(charCount / 160);

  const handleSend = () => {
    alert(`Message sent to ${recipients.length} customers via ${notifyUsing}`);
  };

  return (
    <div className="h-full flex flex-col bg-gray-50" data-testid="send-page">
      {/* Header */}
      <div className="bg-zinc-800 text-white px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {onBack && (
              <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <ArrowLeft size={20} />
              </button>
            )}
            <h1 className="text-xl font-semibold">Send Notification</h1>
          </div>
          <button 
            onClick={handleSend}
            className="flex items-center gap-2 bg-pink-500 hover:bg-pink-600 px-4 py-2 rounded-lg transition-colors"
            data-testid="send-btn"
          >
            <Send size={18} />
            Send
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Recipients */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users size={16} />
              Recipients ({recipients.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {recipients.slice(0, 10).map(customer => (
                <span 
                  key={customer.id}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm"
                >
                  <span 
                    className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs"
                    style={{ backgroundColor: zoneColors[customer.zone] || '#6b7280' }}
                  >
                    {customer.name?.charAt(0)}
                  </span>
                  {customer.name}
                </span>
              ))}
              {recipients.length > 10 && (
                <span className="px-2 py-1 bg-gray-100 rounded-full text-sm text-gray-500">
                  +{recipients.length - 10} more
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Notify Using */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Notify using</span>
              <div className="relative">
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  data-testid="notify-using-dropdown"
                >
                  {notifyOptions.find(o => o.id === notifyUsing)?.label}
                  {notifyUsing === notifyOptions.find(o => o.id === notifyUsing)?.id && (
                    <Check size={16} className="text-green-500" />
                  )}
                  <ChevronDown size={16} className="text-gray-400" />
                </button>
                
                {showDropdown && (
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[150px]">
                    {notifyOptions.map(option => {
                      const Icon = option.icon;
                      return (
                        <button
                          key={option.id}
                          onClick={() => {
                            setNotifyUsing(option.id);
                            setShowDropdown(false);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-50 transition-colors"
                          data-testid={`notify-option-${option.id}`}
                        >
                          <Icon size={16} className="text-gray-400" />
                          <span className="flex-1 text-left">{option.label}</span>
                          {notifyUsing === option.id && (
                            <Check size={16} className="text-green-500" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Character/Credit Count */}
            <div className="mt-3 text-sm text-gray-500">
              {notifyUsing === 'sms' || notifyUsing === 'both' ? (
                <span>{charCount} characters (approximately {smsCredits} credits)</span>
              ) : (
                <span>{charCount} characters ({wordCount} words)</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Subject Line (for email) */}
        {(notifyUsing === 'email' || notifyUsing === 'both') && (
          <Card>
            <CardContent className="pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                placeholder="Email subject line"
                data-testid="email-subject"
              />
            </CardContent>
          </Card>
        )}

        {/* Message Composer */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Message</CardTitle>
              <button className="text-sm text-pink-600 hover:text-pink-700 font-medium">
                Canned Responses
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Formatting Toolbar */}
            <div className="flex items-center gap-1 p-2 bg-gray-50 rounded-t-lg border border-gray-200 border-b-0">
              <button className="p-1.5 hover:bg-gray-200 rounded transition-colors">
                <Undo size={16} className="text-gray-500" />
              </button>
              <button className="p-1.5 hover:bg-gray-200 rounded transition-colors">
                <Redo size={16} className="text-gray-500" />
              </button>
              <div className="w-px h-5 bg-gray-300 mx-1"></div>
              <button className="p-1.5 hover:bg-gray-200 rounded transition-colors">
                <Bold size={16} className="text-gray-500" />
              </button>
              <button className="p-1.5 hover:bg-gray-200 rounded transition-colors">
                <Italic size={16} className="text-gray-500" />
              </button>
              <div className="w-px h-5 bg-gray-300 mx-1"></div>
              <button className="p-1.5 hover:bg-gray-200 rounded transition-colors">
                <AlignLeft size={16} className="text-gray-500" />
              </button>
              <button className="p-1.5 hover:bg-gray-200 rounded transition-colors">
                <AlignCenter size={16} className="text-gray-500" />
              </button>
              <button className="p-1.5 hover:bg-gray-200 rounded transition-colors">
                <AlignRight size={16} className="text-gray-500" />
              </button>
              <div className="w-px h-5 bg-gray-300 mx-1"></div>
              <button className="p-1.5 hover:bg-gray-200 rounded transition-colors">
                <List size={16} className="text-gray-500" />
              </button>
              <button className="p-1.5 hover:bg-gray-200 rounded transition-colors">
                <Link size={16} className="text-gray-500" />
              </button>
              <button className="p-1.5 hover:bg-gray-200 rounded transition-colors">
                <Image size={16} className="text-gray-500" />
              </button>
            </div>

            {/* Message Text Area */}
            <textarea
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              className="w-full h-48 px-4 py-3 border border-gray-200 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-pink-500 resize-none"
              placeholder="Type your message here..."
              data-testid="message-content"
            />

            {/* Merge Tags */}
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-2">Available merge tags:</p>
              <div className="flex flex-wrap gap-2">
                {['[firstname]', '[lastname]', '[balance]', '[address]', '[nextjob]', '[company]'].map(tag => (
                  <button
                    key={tag}
                    onClick={() => setMessageContent(prev => prev + ' ' + tag)}
                    className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs text-gray-600 transition-colors"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {/* Email Header with Logo */}
              {(notifyUsing === 'email' || notifyUsing === 'both') && (
                <div className="bg-zinc-800 p-6 text-center">
                  {companyLogo ? (
                    <img 
                      src={companyLogo} 
                      alt={companyName}
                      className="w-16 h-16 rounded-xl object-cover mx-auto"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-emerald-500 rounded-xl flex items-center justify-center mx-auto">
                      <span className="text-white font-bold text-xl">
                        {companyName.substring(0, 2).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <h2 className="text-white font-semibold mt-3">{companyName}</h2>
                </div>
              )}
              
              <div className="p-4">
                {(notifyUsing === 'email' || notifyUsing === 'both') && (
                  <div className="mb-3 pb-3 border-b border-gray-100">
                    <p className="text-xs text-gray-500">Subject:</p>
                    <p className="font-medium">{subject}</p>
                  </div>
                )}
                <div className="whitespace-pre-wrap text-sm text-gray-700">
                  {messageContent.replace('[firstname]', 'John')}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-emerald-600">
                    {companyLogo ? (
                      <img 
                        src={companyLogo} 
                        alt={companyName}
                        className="w-8 h-8 rounded object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-emerald-100 rounded flex items-center justify-center">
                        <span className="text-xs font-bold">{companyName.substring(0, 2).toUpperCase()}</span>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-semibold">{companyName}</p>
                      <p className="text-xs text-gray-500">Window Cleaning Services</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Word count footer */}
        <div className="text-center text-sm text-gray-500">
          {wordCount} WORDS
        </div>
      </div>
    </div>
  );
};

export default SendPage;
